import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import { useCallback, useMemo, useState } from "react";

const RECENT_SEARCHES_KEY = "loanSearchRecent";
const MAX_RECENT = 6;

const getRecentSearches = () => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveRecentSearch = (loanNumber) => {
  if (!loanNumber) return;
  try {
    const recent = getRecentSearches();
    const filtered = recent.filter((v) => v !== loanNumber);
    const updated = [loanNumber, ...filtered].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  } catch {
    // silent fail
  }
};

const LoanSearchAutocomplete = ({
  value,
  onChange,
  fullWidth = true,
  sx,
  options,
  size = "medium",
  placeholder,
  autoFocus = false,
  onBlur,
  error,
  helperText,
}) => {
  const [inputValue, setInputValue] = useState("");

  const selectedOption = useMemo(() => {
    return (
      options?.find((option) =>
        isString(option) ? option === value : option.value === value,
      ) || null
    );
  }, [value, options]);

  const recentValues = useMemo(() => getRecentSearches(), []);

  // Build the grouped options list: recent searches first, then all options
  const groupedOptions = useMemo(() => {
    const isTyping = inputValue.trim().length > 0;

    // When typing, just return flat options (no grouping) so the filter works normally
    if (isTyping) {
      return options.map((opt) => ({ ...opt, group: "Results" }));
    }

    // When not typing, show recent searches on top
    const recentOptions = recentValues
      .map((val) =>
        options.find((o) => (isString(o) ? o === val : o.value === val)),
      )
      .filter(Boolean)
      .map((opt) => ({
        ...opt,
        group: "Recent Searches",
        _isRecent: true,
      }));

    const recentValuesSet = new Set(recentValues);
    const otherOptions = options
      .filter((o) => !recentValuesSet.has(isString(o) ? o : o.value))
      .map((opt) => ({ ...opt, group: "All Loans" }));

    return [...recentOptions, ...otherOptions];
  }, [options, recentValues, inputValue]);

  const handleChange = useCallback(
    (event, newValue) => {
      const selected = isString(newValue) ? newValue : newValue?.value;
      if (selected) saveRecentSearch(selected);
      onChange(selected);
    },
    [onChange],
  );

  const styles = useMemo(() => {
    return {
      ".MuiAutocomplete-endAdornment": {
        marginRight: "0px",
      },
      "& .MuiInputBase-input": {
        color: selectedOption ? "inherit" : "rgba(0, 0, 0, 0.3)",
      },
      ...sx,
    };
  }, [sx, selectedOption]);

  const emptyPlaceholderValue = placeholder || "Start Typing...";

  return (
    <Autocomplete
      loading={isNil(options) || isEmpty(options)}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
      autoFocus={autoFocus}
      onBlur={onBlur}
      onChange={handleChange}
      isOptionEqualToValue={(option, val) => {
        const optVal = isString(option) ? option : option.value;
        const valVal = isString(val) ? val : val.value;
        return optVal === valVal;
      }}
      size={size}
      options={groupedOptions}
      groupBy={(option) => option.group}
      sx={styles}
      getOptionLabel={(option) => {
        if (isString(option)) return option;
        return option.label !== undefined
          ? String(option.label)
          : String(option.value ?? "");
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <Typography
            sx={{
              fontSize: ".65rem",
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: ".5px",
              px: 1.5,
              py: 0.75,
              position: "sticky",
              top: -8,
              backgroundColor: "background.paper",
              zIndex: 1,
            }}
          >
            {params.group}
          </Typography>
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const displayText =
          option.optionLabel || option.label || String(option.value ?? "");
        return (
          <Box
            key={`${option.group}-${option.value}`}
            sx={{ fontSize: ".7rem" }}
            {...optionProps}
          >
            {displayText}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          ref={params.InputProps.ref}
          autoFocus={autoFocus}
          placeholder={emptyPlaceholderValue}
          {...params}
          helperText={helperText}
          error={error}
          inputProps={{ ...params.inputProps }}
        />
      )}
    />
  );
};

export default LoanSearchAutocomplete;
